// Cost-band refresh evidence — P3 (docs/implementation/P3_AUDIT_2026-09-12.md
// finding 2: "catalog price refresh erases evidence"). Pure helpers over the
// rows sp_GetEquipmentCostBands (db/50) returns — a band's LIVE statistics
// (QuoteCount / AvgUnitCost over today's snapshots) beside its LAST APPLIED
// refresh (LastRefresh*) and the catalog row's PriceEvidenceRefreshID (the
// refresh that set the price it carries now; NULL once someone edits the
// price by hand). Nothing here writes; "Refresh from Quotes" stays an
// explicit, confirmed admin action.
//
//   state
//     never         no applied refresh — the catalog price was typed, not derived
//     current       the catalog price is the last refresh's number and today's
//                   snapshots still give the same count and average
//     price_edited  a refresh set a price, but the row no longer carries it
//                   (hand-edited since, or a different refresh) — the evidence
//                   link is broken on purpose
//     quotes_changed the row still carries the refresh's number, but the quote
//                   population in the band has changed since — a refresh would
//                   move the price
//     unset         the band has no linked catalog row

const num = (v) => { if (v === null || v === undefined || v === "") return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
export const PRICE_TOL = 0.005;

export function bandRefreshState(band) {
  if (!band) return { state: "unset", label: "—", detail: "" };
  if (band.ItemID == null && band.AssemblyID == null) return { state: "unset", label: "No catalog row", detail: "Link an Item / Assembly before refreshing." };
  const lastId = band.LastRefreshID ?? null;
  if (lastId == null) return { state: "never", label: "Never refreshed", detail: band.CurrentMaterialCostPerUnit != null ? "The current price was entered by hand — no quote evidence behind it." : "No price yet." };
  const current = num(band.CurrentMaterialCostPerUnit), lastPrice = num(band.LastRefreshNewPrice);
  const linked = band.PriceEvidenceRefreshID != null && band.PriceEvidenceRefreshID === lastId && (current === null || lastPrice === null || Math.abs(current - lastPrice) <= PRICE_TOL);
  const when = band.LastRefreshedAt ? String(band.LastRefreshedAt).slice(0, 10) : "?";
  const who = band.LastRefreshedBy ? ` by ${band.LastRefreshedBy}` : "";
  if (!linked) return { state: "price_edited", label: "Edited since refresh", detail: `Refreshed ${when}${who} to $${fmt(lastPrice)} from ${band.LastRefreshQuoteCount ?? "?"} quote(s); the row now carries $${fmt(current)} — set by hand, not by that evidence.` };
  const liveCount = num(band.QuoteCount) ?? 0, liveAvg = num(band.AvgUnitCost);
  const changed = liveCount !== (num(band.LastRefreshQuoteCount) ?? 0) || (liveAvg !== null && lastPrice !== null && Math.abs(liveAvg - lastPrice) > PRICE_TOL);
  if (changed) return { state: "quotes_changed", label: "Quotes changed", detail: `Price from ${band.LastRefreshQuoteCount ?? "?"} quote(s) on ${when}${who}; today ${liveCount} quote(s) average $${fmt(liveAvg)} — a refresh would move it.` };
  return { state: "current", label: "Evidence current", detail: `$${fmt(current)} from ${band.LastRefreshQuoteCount ?? "?"} quote(s), refreshed ${when}${who}; today's snapshots agree.` };
}
const fmt = (v) => v == null ? "—" : (+v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Group sp_GetEquipmentCostBandRefreshes' Table2 rows under their refresh (Table1).
export function attachEvidence(refreshes = [], evidenceRows = []) {
  const byRefresh = new Map();
  for (const e of evidenceRows) (byRefresh.get(e.RefreshID) || byRefresh.set(e.RefreshID, []).get(e.RefreshID)).push(e);
  return refreshes.map(r => ({ ...r, evidence: byRefresh.get(r.RefreshID) || [], evidenceMissing: Math.max(0, (num(r.QuoteCount) ?? 0) - (byRefresh.get(r.RefreshID) || []).length) }));
}

// The EXEC statement's plain parameters, ''-escaped (the sqlGuard shape).
export function refreshBandStatement(bandId, { refreshedBy = null, note = null } = {}) {
  const esc = (v) => String(v).replace(/'/g, "''");
  let sql = `EXEC sp_RefreshEquipmentCostBandPricing @band_id=${+bandId}`;
  if (refreshedBy) sql += `, @refreshed_by='${esc(refreshedBy)}'`;
  if (note) sql += `, @note='${esc(note)}'`;
  return sql;
}
